import { useEffect, useMemo } from "react";
import { useConfig } from "../../../../providers/useConfig";
import { getInitialDataFromURL } from "../../utils/convertSharedFilterData";
import { useFlotiqStores } from "../useFlotiqStores/useFlotiqStores";
import { useGoogleMapsStoreLocator } from "../useGoogleMapsStoreLocator";
import { useNaverMapsStoreLocator } from "../useNaverMapsStoreLocator/useNaverMapsStoreLocator";
import { useStoreFilters } from "../useStoreFilters";
import { useTimedToggle } from "../useTimedToggle";
import { useYandexMapsStoreLocator } from "../useYandexMapsStoreLocator/useYandexMapsStoreLocator";
const SECOND = 1000;
const DEFAULT_MAP_INACTIVITY_TIMEOUT = 3 * SECOND;
export const useStoreLocator = ({ defaultLat, defaultLng, storesLimit, defaultZoom, activeStore, cartItems, filterByCartAvailabilityEnabled, filtersTranslations, filtersEnabled, isFiltersSharingEnabled = false, storeDetailsPage, storeDetailsPageTarget = "_blank", onChooseStore, activeStoreId, flotiqApiKey, flotiqContentType, defaultStoreIcon, storeIcons, isSearchVisible = true, isOpeningHoursEnabled = false, isOpeningHoursBreaksEnabled = false, isAvailableSticksEnabled = false, isAvailableDevicesEnabled = false, isMultipleFiltersSectionOpenEnabled = false, isGeneralAnnouncementEnabled = false, isAvailableServicesEnabled = false, isStoreTypeEnabled = false, isVendingMachineEnabled = false, defaultOpenedFilterCategory, enabledFiltersCategories = {
    isOpenNowFilterEnabled: false,
    isProductColorsFilterEnabled: false,
    isProductFlavoursFilterEnabled: false,
    isProductTypesFilterEnabled: true,
    isStoreServicesFilterEnabled: true,
    isStoreTypeFilterEnabled: false,
}, searchAdditionalTitle, isInputExtraTextVisible = false, isMapInteractionEnabled = false, mapInactivityTimeout = DEFAULT_MAP_INACTIVITY_TIMEOUT, }) => {
    const { active: isMapActive, toggle: toggleIsMapActive, off: setMapInactive } = useTimedToggle(mapInactivityTimeout);
    const { locationServiceMapProvider } = useConfig();
    const mapStoreLocator = useMemo(() => {
        switch (locationServiceMapProvider) {
            case "google":
                return useGoogleMapsStoreLocator;
            case "naver":
                return useNaverMapsStoreLocator;
            case "yandex":
                return useYandexMapsStoreLocator;
            default:
                return useGoogleMapsStoreLocator;
        }
    }, [locationServiceMapProvider]);
    const flotiqStoresData = useFlotiqStores({
        cartItems,
        filterByCartAvailabilityEnabled,
        filtersTranslations,
        storesLimit: storesLimit,
        activeStore,
        activeStoreId,
        flotiqApiKey,
        flotiqContentType,
        enabledFiltersCategories,
        isFiltersSharingEnabled,
    });
    const initialMapPosition = useMemo(() => {
        const initialData = getInitialDataFromURL(isFiltersSharingEnabled);
        const mapPosition = {
            defaultLat,
            defaultLng,
            defaultZoom,
        };
        if (isFiltersSharingEnabled && initialData?.mapPosition) {
            const { lat, lng, zoom } = initialData.mapPosition;
            if (lat && lng && zoom) {
                mapPosition.defaultLat = lat.toString();
                mapPosition.defaultLng = lng.toString();
                mapPosition.defaultZoom = zoom;
            }
        }
        return mapPosition;
    }, [defaultLat, defaultLng, defaultZoom, isFiltersSharingEnabled]);
    const storeLocatorData = mapStoreLocator({
        ...initialMapPosition,
        activeStoreId: flotiqStoresData.activeStoreId,
        storeDetailsPage,
        handleSelectStore: flotiqStoresData.handleSelectStore,
        handleLoadFlotiqStores: flotiqStoresData.getFlotiqStores,
        isStoreDetails: !!activeStore,
        defaultStoreIcon,
        storeIcons,
        isSearchVisible,
        onActivity: toggleIsMapActive,
        onTap: setMapInactive,
        isNoStoresFilters: flotiqStoresData.isNoStoresFilters,
    });
    useEffect(() => {
        storeLocatorData.handleLoadMarkers(flotiqStoresData.flotiqStores || []);
    }, [flotiqStoresData.flotiqStores]);
    useEffect(() => {
        if (flotiqStoresData.userMarkerPosition) {
            storeLocatorData.handleLoadUserMarker(flotiqStoresData.userMarkerPosition.lat, flotiqStoresData.userMarkerPosition.lng);
        }
    }, [flotiqStoresData.userMarkerPosition]);
    const activeFiltersNumber = useMemo(() => {
        const { selectedFilters } = flotiqStoresData;
        return selectedFilters ? Object.values(selectedFilters)?.filter(({ selected }) => selected).length : 0;
    }, [flotiqStoresData]);
    const storeFilters = useStoreFilters({
        handleChangeFilters: flotiqStoresData.handleChangeFilters,
        isOpenNowFilterEnabled: enabledFiltersCategories.isOpenNowFilterEnabled,
        selectedFilters: flotiqStoresData.selectedFilters,
        allFilters: flotiqStoresData.newFilters,
    });
    return {
        ...flotiqStoresData,
        ...storeLocatorData,
        ...storeFilters,
        ...enabledFiltersCategories,
        handleSelectStore: storeLocatorData?.handleSelectStoreFromList ?? flotiqStoresData.handleSelectStore,
        storeDetailsPage,
        storeDetailsPageTarget,
        filtersEnabled,
        selectedFiltersNumber: activeFiltersNumber,
        searchAdditionalTitle,
        onChooseStore,
        isMultipleFiltersSectionOpenEnabled,
        defaultOpenedFilterCategory,
        isOpeningHoursEnabled,
        isOpeningHoursBreaksEnabled,
        isAvailableSticksEnabled,
        isAvailableDevicesEnabled,
        isMapInteractionEnabled,
        isInputExtraTextVisible,
        isGeneralAnnouncementEnabled,
        isAvailableServicesEnabled,
        isStoreTypeEnabled,
        isVendingMachineEnabled,
        isMapActive: isMapActive && isMapInteractionEnabled,
    };
};
//# sourceMappingURL=useStoreLocator.js.map